/**
 * The parts both transfer popups share (wireframe admin-console-wireframe: "Transfer ownership" and "Transfer
 * everything from a person"), built from existing pieces: the New owner SbSelect over the whole HR pool (people who
 * can't be picked are disabled options with the reason), the one "Existing shares and delegations" choice (SbRadio),
 * and the permanence banner (the PAIC transfer popup's warning box). Kept as one file so the two popups don't repeat
 * them.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Flex } from 'antd';
import { SbRadio, SbSelect, SbSpin, WarningFilled } from 'uiCore';
import {
  TransferFieldLabel, TransferHelperText, TransferWarningBox, TransferWarningText,
} from 'Pages/PeoplePlusAIV2/dashboard/ManageGroupsModal.styles';
import {
  CARRY_LABEL, CARRY_OPTIONS, NEW_OWNER_LABEL, NEW_OWNER_PLACEHOLDER, getAffectedLine, getCandidateOptions,
} from 'Utils/ownershipTransfer';

// error: the list couldn't be loaded (said as ReassignApproverModal does; the select is then disabled)
export const NewOwnerField = ({ candidates = null, loading = false, value = null, onChange, disabled = false,
  helpAbove = null, helpBelow = null, error = null }) => (
  <>
    <TransferFieldLabel>{NEW_OWNER_LABEL}</TransferFieldLabel>
    {helpAbove && <TransferHelperText>{helpAbove}</TransferHelperText>}
    {loading ? <SbSpin size="small" /> : (
      <SbSelect
        options={getCandidateOptions(candidates || {})}
        value={value || undefined}
        onChange={(v) => onChange(v || null)}
        placeholder={NEW_OWNER_PLACEHOLDER}
        showSearch
        optionFilterProp="label"
        allowClear
        disabled={disabled || !!error}
        style={{ width: '100%' }}
        getPopupContainer={(trigger) => trigger.parentNode}
        aria-label={NEW_OWNER_LABEL}
      />
    )}
    {error ? <TransferHelperText data-testid="owner-error">{error}</TransferHelperText>
      : helpBelow && <TransferHelperText data-testid="owner-help">{helpBelow}</TransferHelperText>}
  </>
);

NewOwnerField.propTypes = {
  candidates: PropTypes.shape({ candidates: PropTypes.array, excluded: PropTypes.array }),
  loading: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  helpAbove: PropTypes.string,
  helpBelow: PropTypes.string,
  error: PropTypes.string,
};

export const CarryChoice = ({ value, onChange, affectedNames = [] }) => (
  <>
    <TransferFieldLabel>{CARRY_LABEL}</TransferFieldLabel>
    <Flex vertical gap={6} role="radiogroup" aria-label={CARRY_LABEL}>
      {CARRY_OPTIONS.map((option) => (
        <SbRadio key={option.value} value={option.value} checked={value === option.value}
          onChange={() => onChange(option.value)} aria-label={option.title}>
          <b>{option.title}</b> – {option.text}
        </SbRadio>
      ))}
    </Flex>
    <TransferHelperText data-testid="carry-affected">{getAffectedLine(affectedNames)}</TransferHelperText>
  </>
);

CarryChoice.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  affectedNames: PropTypes.arrayOf(PropTypes.string),
};

export const PermanenceBanner = ({ text }) => (
  <TransferWarningBox role="note">
    <WarningFilled />
    <TransferWarningText>{text}</TransferWarningText>
  </TransferWarningBox>
);

PermanenceBanner.propTypes = { text: PropTypes.string.isRequired };
